import React from 'react';
import { X, Upload, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Button } from '../../ui/Button';
import { moduleApi } from '../../../lib/api';
import { allergensOfIngredient } from '../../../../shared/allergenReference.js';
import type { DishIngredient } from './MenuCatalogEditors';
import { useConfirm } from '../../ui/ConfirmProvider';

interface ParsedDish {
    category: string;
    name: string;
    description: string;
    ingredients: DishIngredient[];
    traces: string[];
}
interface ParsedMenu { name: string; dishes: ParsedDish[]; }

// En-têtes reconnus, comparés sans casse ni accents : le fichier peut varier d'une version à l'autre
// ("Ingrédients Bruts Détaillés par Élément du Plat", "Ingrédients Bruts (…)"), l'import ne doit pas casser
// pour un libellé légèrement différent.
const norm = (s: any) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const findCol = (header: any[], ...keys: string[]) =>
    header.findIndex((h) => keys.some((k) => norm(h).includes(k)));

/**
 * Import d'une carte depuis un classeur Excel.
 *
 * Une feuille = une formule, ses lignes = les plats. La colonne des ingrédients suit le format par
 * sous-composants d'Emeni, qui correspond exactement au modèle du logiciel :
 *
 *     [Filet de Bœuf Saisi]                 -> un ingrédient
 *     • Filet de bœuf, beurre, ail, thym    -> ses composants
 *     [Jus Réduit au Vin Rouge]             -> un autre ingrédient
 *     • Vin rouge, os de veau, échalote
 *
 * Rien n'est écrit en base avant validation : l'écran montre d'abord ce qui a été compris, plat par plat,
 * avec les allergènes détectés et ce qui reste non reconnu. Un import qui se tromperait en silence sur une
 * carte entière serait pire qu'une saisie manuelle.
 */
export function MenuCatalogImport({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
    const [menus, setMenus] = React.useState<ParsedMenu[]>([]);
    const [fileName, setFileName] = React.useState('');
    const confirmAction = useConfirm();
    const [error, setError] = React.useState('');
    const [importing, setImporting] = React.useState(false);
    const [report, setReport] = React.useState('');
    const [createMenus, setCreateMenus] = React.useState(true);

    const parseIngredients = (raw: string): DishIngredient[] => {
        const text = String(raw || '');
        const out: DishIngredient[] = [];
        // Découpe sur chaque "[Élément]" en début de ligne ; ce qui suit jusqu'au bloc suivant est sa liste.
        const blocks = text.split(/\n(?=\s*\[)/);
        blocks.forEach((block) => {
            const m = block.match(/^\s*\[(.+?)\]\s*([\s\S]*)$/);
            if (!m) return;
            const name = m[1].trim();
            const components = m[2].replace(/^[\s•\-*]+/gm, '').split(/[,\n]/)
                .map((x) => x.trim().replace(/\.$/, '')).filter(Boolean)
                .map((name) => ({ name, removable: false }));
            if (!name) return;
            // Une garniture ou un topping se retire sans refaire le plat : coché par défaut, modifiable
            // ensuite dans la fiche. Le reste ne l'est pas — mieux vaut un retrait oublié qu'un retrait
            // promis au convive que la cuisine ne peut pas honorer.
            const removable = /topping|garniture|decoration|d[ée]coration|sauce|emulsion|[ée]mulsion|condiment/i.test(name);
            out.push({ name, removable, components, incompatible_tags: [] });
        });
        // Repli : pas de format par éléments, on prend la liste à plat.
        if (!out.length && text.trim()) {
            text.split(/[,\n]/).map((x) => x.trim().replace(/\.$/, '')).filter(Boolean)
                .forEach((n) => out.push({ name: n, removable: false, components: [], incompatible_tags: [] }));
        }
        return out;
    };

    const handleFile = async (file: File) => {
        setError(''); setReport(''); setMenus([]); setFileName(file.name);
        try {
            const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
            const parsed: ParsedMenu[] = [];
            wb.SheetNames.forEach((sheetName) => {
                const rows: any[][] = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { header: 1, blankrows: false, defval: '' });
                const hIdx = rows.findIndex((r) => findCol(r, 'categorie') >= 0 && findCol(r, 'nom du plat', 'intitule') >= 0);
                if (hIdx < 0) return;
                const h = rows[hIdx];
                const cCat = findCol(h, 'categorie');
                const cName = findCol(h, 'nom du plat', 'intitule');
                const cDesc = findCol(h, 'description');
                const cIng = findCol(h, 'ingredient');
                const cTraces = findCol(h, 'trace');
                const cRemov = findCol(h, 'retirable', 'retirer');

                const dishes: ParsedDish[] = [];
                rows.slice(hIdx + 1).forEach((r) => {
                    const cat = String(r[cCat] ?? '').trim();
                    const name = String(r[cName] ?? '').trim();
                    if (!name || !/entr[ée]e|plat|dessert/i.test(cat)) return;
                    const ingredients = parseIngredients(String(r[cIng] ?? ''));
                    // Colonne "retirable" facultative : elle nomme les éléments entre crochets à décocher.
                    if (cRemov >= 0) {
                        const wanted = String(r[cRemov] ?? '').split(/[,\n]/).map((x) => norm(x)).filter(Boolean);
                        if (wanted.length) ingredients.forEach((i) => { i.removable = wanted.some((w) => norm(i.name).includes(w) || w.includes(norm(i.name))); });
                    }
                    const traces = cTraces >= 0
                        ? String(r[cTraces] ?? '').split(/[,\n]/).map((x) => x.trim().replace(/\.$/, '')).filter(Boolean)
                        : [];
                    dishes.push({
                        category: cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase(),
                        name, description: String(r[cDesc] ?? '').trim(), ingredients, traces,
                    });
                });
                if (dishes.length) parsed.push({ name: sheetName.trim(), dishes });
            });
            if (!parsed.length) { setError("Aucun plat trouvé. Le fichier doit avoir une ligne d'en-tête avec « Catégorie » et « Nom du Plat »."); return; }
            setMenus(parsed);
        } catch (e: any) {
            setError(e?.message || 'Fichier illisible.');
        }
    };

    const totalDishes = menus.reduce((n, m) => n + m.dishes.length, 0);
    const unknown = React.useMemo(() => {
        const set = new Set<string>();
        menus.forEach((m) => m.dishes.forEach((d) => d.ingredients.forEach((i) => {
            if (allergensOfIngredient(i).length === 0) set.add(i.name);
        })));
        return Array.from(set);
    }, [menus]);

    const runImport = async () => {
        if (!(await confirmAction({ title: 'Lancer l\'import ?', description: <>{totalDishes} plat(s){createMenus ? ` et ${menus.length} formule(s)` : ''} seront créés. Les plats déjà présents portant le même nom seront mis à jour, sans rien perdre de ce qui n'est pas dans le fichier.</>, confirmLabel: 'Importer', destructive: false }))) return;
        setImporting(true); setError('');
        try {
            const existing: any[] = await moduleApi.getMenuItems().catch(() => []);
            const byName = new Map(existing.map((i: any) => [norm(i.name), i]));
            const idByName = new Map<string, string>();
            let created = 0, updated = 0;

            for (const m of menus) {
                for (const d of m.dishes) {
                    const found = byName.get(norm(d.name));
                    const payload: any = {
                        name: d.name, category: d.category, description: d.description,
                        ingredients: d.ingredients, traces: d.traces,
                    };
                    if (found) {
                        // Mise à jour SANS écraser ce qui n'est pas dans le fichier : les traces déjà
                        // saisies sont conservées si la colonne est absente, comme convenu avec Emeni
                        // (« compléter, ne jamais écraser »).
                        if (!d.traces.length) delete payload.traces;
                        await moduleApi.updateMenuItem(found.id, payload);
                        idByName.set(norm(d.name), found.id);
                        updated++;
                    } else {
                        const res: any = await moduleApi.createMenuItem(payload);
                        if (res?.id) idByName.set(norm(d.name), res.id);
                        created++;
                    }
                }
            }

            let menusCreated = 0;
            if (createMenus) {
                const existingMenus: any[] = await moduleApi.getMenus().catch(() => []);
                const menuByName = new Map(existingMenus.map((x: any) => [norm(x.name), x]));
                for (const m of menus) {
                    const byCat = new Map<string, string[]>();
                    m.dishes.forEach((d) => {
                        const id = idByName.get(norm(d.name));
                        if (!id) return;
                        if (!byCat.has(d.category)) byCat.set(d.category, []);
                        byCat.get(d.category)!.push(id);
                    });
                    const structure = Array.from(byCat.entries()).map(([category, dish_ids]) => ({ category, dish_ids }));
                    const found = menuByName.get(norm(m.name));
                    if (found) await moduleApi.updateMenu(found.id, { name: m.name, structure });
                    else await moduleApi.createMenu({ name: m.name, structure });
                    menusCreated++;
                }
            }
            setReport(`${created} plat(s) créé(s), ${updated} mis à jour${createMenus ? `, ${menusCreated} formule(s)` : ''}.`);
            onDone();
        } catch (e: any) {
            setError(e?.message || "Erreur pendant l'import.");
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" onClick={onClose}>
            <div className="bg-surface border border-border w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] sm:max-h-[88vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border shrink-0">
                    <h3 className="text-sm font-bold text-content">Importer une carte (Excel)</h3>
                    <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-surface-soft text-content-muted hover:text-content"><X size={16} /></button>
                </div>

                <div className="overflow-y-auto px-4 py-3.5 flex-1 space-y-3">
                    <p className="text-[11px] text-content-muted">
                        Une feuille = une formule, ses lignes = les plats. Les ingrédients sont lus au format par sous-composants :
                        chaque <code className="text-content">[Élément]</code> devient un ingrédient, la liste qui suit ses composants.
                        Colonnes facultatives : « Retirable » (les éléments qu'on peut ôter) et « Traces possibles ».
                    </p>

                    <label className="flex items-center justify-center gap-2 border border-dashed border-border rounded-xl py-6 cursor-pointer hover:border-brand-500 text-sm font-bold text-content-muted hover:text-content">
                        <Upload size={16} /> {fileName || 'Choisir un fichier .xlsx'}
                        <input type="file" accept=".xlsx,.xls" className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
                    </label>

                    {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                    {report && (
                        <p className="text-xs text-green-700 dark:text-green-400 font-semibold bg-green-500/10 border border-green-500/30 rounded-lg px-2.5 py-2 flex items-start gap-1.5">
                            <CheckCircle2 size={13} className="shrink-0 mt-0.5" /> {report}
                        </p>
                    )}

                    {menus.length > 0 && !report && (
                        <>
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                <p className="text-xs font-bold text-content">{totalDishes} plat(s) dans {menus.length} formule(s)</p>
                                <label className="flex items-center gap-1.5 text-[11px] text-content cursor-pointer">
                                    <input type="checkbox" checked={createMenus} onChange={(e) => setCreateMenus(e.target.checked)}
                                        className="w-3.5 h-3.5 rounded border-border accent-brand-600" />
                                    Créer aussi les formules
                                </label>
                            </div>

                            {unknown.length > 0 && (
                                <p className="text-[10px] text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-2 py-1.5 flex items-start gap-1.5">
                                    <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                                    <span>
                                        {unknown.length} élément(s) sans allergène détecté : {unknown.slice(0, 6).join(', ')}{unknown.length > 6 ? '…' : ''}.
                                        Beaucoup sont inoffensifs. Après l'import, « Contrôler le catalogue » permet de rattacher ceux qui le méritent.
                                    </span>
                                </p>
                            )}

                            {menus.map((m) => (
                                <div key={m.name} className="bg-surface-soft border border-border rounded-xl p-2.5 space-y-1.5">
                                    <p className="text-xs font-bold text-content">{m.name} — {m.dishes.length} plat(s)</p>
                                    {m.dishes.map((d) => {
                                        const alg = new Set<string>();
                                        d.ingredients.forEach((i) => allergensOfIngredient(i).forEach((a: string) => alg.add(a)));
                                        return (
                                            <div key={d.name} className="text-[10px] text-content-muted border-t border-border pt-1">
                                                <span className="font-bold text-content">{d.category} · {d.name}</span>
                                                <span className="block">
                                                    {d.ingredients.length} élément(s) — {d.ingredients.filter((i) => i.removable).length} retirable(s)
                                                    {alg.size > 0 && <> · <span className="text-warning-600 dark:text-warning-400">{Array.from(alg).join(', ')}</span></>}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border shrink-0">
                    <Button variant="secondary" size="sm" onClick={onClose}>{report ? 'Fermer' : 'Annuler'}</Button>
                    {menus.length > 0 && !report && (
                        <Button size="sm" onClick={runImport} disabled={importing}>
                            {importing ? <><Loader2 size={12} className="animate-spin" /> Import…</> : `Importer ${totalDishes} plat(s)`}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
